import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import {
    getProjectByIdAsync,
    ProjectResponse,
    getProjectDocumentsAsync,
    DocumentListResponse,
    getDocumentByIdAsync,
    DocumentInfo,
    getAvailableModelsAsync,
    ModelsResponse,
    RegionResponse,
    getAvailableRegionsAsync,
} from "./api";

export function useMeetings(projectId: string | undefined) {
    return useQuery({
        ...queryKeys.projectAccess.byProjectId(projectId || ""),
        queryFn: () => {
            return [];
        },
    });
}

export function useTranscriptionProjectDocuments(projectId: string | undefined) {
    return useQuery<DocumentListResponse | undefined>({
        ...queryKeys.documents.byProjectId(projectId || ""),
        queryFn: async () => {
            if (!projectId) {
                return undefined;
            }
            return await getProjectDocumentsAsync(projectId);
        },
        enabled: !!projectId,
        staleTime: 0,
        gcTime: 0,
    });
}

export function useProject(projectId?: string) {
    return useQuery<ProjectResponse | undefined>({
        ...queryKeys.projectAccess.byProjectId(projectId || ""),
        queryFn: async () => {
            if (!projectId) {
                return undefined;
            }
            return await getProjectByIdAsync(projectId);
        },
        enabled: !!projectId,
    });
}

export function useDocument(documentId?: string) {
    return useQuery<DocumentInfo | undefined>({
        queryKey: ["document", documentId],
        queryFn: async () => {
            if (!documentId) {
                return undefined;
            }
            return await getDocumentByIdAsync(documentId);
        },
        enabled: !!documentId,
    });
}

export function useAvailableModels() {
    return useQuery<ModelsResponse>({
        queryKey: ["models"],
        queryFn: async () => {
            return await getAvailableModelsAsync();
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export function useAvailableRegions() {
    return useQuery<RegionResponse>({
        queryKey: ["regions"],
        queryFn: async () => {
            return await getAvailableRegionsAsync();
        },
        staleTime: Infinity, // Cache for entire session - regions rarely change
    });
}

export function useConversationList(projectId: string) {
    return useQuery({
        ...queryKeys.projectAccess.byProjectId(projectId),
        queryFn: () => {
            return [];
        },
    });
}

export function useCreateTranscriptionConversation() {
    return [];
}

export function useConversationMessages(projectId: string) {
    return useQuery({
        ...queryKeys.projectAccess.byProjectId(projectId),
        queryFn: () => {
            return [
                {
                    id: "1",
                    role: "system",
                    content: "Hello, how can I help you?",
                    created_datetime: new Date().toISOString(),
                    updated_datetime: new Date().toISOString(),
                },
            ];
        },
    });
}
